import { XCHAIN_STAGES } from "@decibeltrade/utils";
import { useQuery } from "@tanstack/react-query";
import z from "zod/v4";

import { ERROR_BACKOFF_MS } from "@/contexts/queries/backoff";
import { env } from "@/env";
import { authenticatedRequest, type GetAuthHeaders } from "@/utils/signature-auth";

// Mirror of XchainDepositSchema in typescript/server/src/routes/xchain.route.ts — keep the
// field list identical when the server DTO changes.
const XchainDepositSchema = z.object({
  /**
   * Identity of one delivery — the destination-chain transaction hash, not a deposit address.
   *
   * One address funds an Intents account that accrues every deposit the user ever makes, so the
   * address identifies the account rather than the money that arrived in it.
   */
  id: z.string(),
  /** Which collector delivered this, e.g. "usdc". Not the chain the funds came from — unknowable. */
  destinationAssetKey: z.string(),
  stage: z.enum(XCHAIN_STAGES),
  status: z.string(),
  updatedAt: z.string().nullable(),
  /** Already net of `withdrawFeeFormatted` — never subtract that fee from this a second time. */
  amountOutFormatted: z.string().nullable(),
  /** USD value of `amountOutFormatted` at delivery. Null until the delivery settles. */
  amountOutUsd: z.string().nullable(),
  /**
   * What 1Click charged this delivery, in display units of the destination asset. Only known
   * once the ANY_INPUT collector's sweep has settled — never derived client-side by diffing an
   * input and output amount.
   */
  withdrawFeeFormatted: z.string().nullable(),
  withdrawFeeUsd: z.string().nullable(),
  destinationTxHash: z.string().nullable(),
  /**
   * Carried on the delivery rather than joined against the asset list: a delivery does not say
   * which chain funded it, so there is no origin row to join to.
   */
  destinationSymbol: z.string(),
  destinationFaAddress: z.string(),
  destinationDecimals: z.number(),
  sweepable: z.boolean(),
});

const DepositsResponseSchema = z.discriminatedUnion("success", [
  z.object({
    success: z.literal(true),
    data: z.object({ deposits: z.array(XchainDepositSchema) }),
  }),
  z.object({ success: z.literal(false), error: z.string() }),
]);

export type XchainDeposit = z.infer<typeof XchainDepositSchema>;

/**
 * Scoped to the account whose SIWA session the server reads the deposits from. Without it a
 * wallet switch would serve the previous account's deposits from cache, and the tracker would
 * import them as cards pointing at the *new* account's subaccount.
 */
export function xchainDepositsQueryKey(accountAddress: string | undefined) {
  return ["xchainDeposits", accountAddress] as const;
}

export function useXchainDepositsQuery({
  accountAddress,
  getAuthHeaders,
  subaccountAddress,
  enabled = true,
}: {
  accountAddress: string | undefined;
  getAuthHeaders: GetAuthHeaders;
  subaccountAddress?: string;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: xchainDepositsQueryKey(accountAddress),
    queryFn: async (): Promise<XchainDeposit[]> => {
      const response = await authenticatedRequest.get({
        schema: DepositsResponseSchema,
        url: `${env.NEXT_PUBLIC_API_URL}/api/xchain/deposits`,
        getAuthHeaders,
        subaccountAddress,
      });
      if (!response.data.success) {
        throw new Error(response.data.error);
      }
      return response.data.data.deposits;
    },
    enabled: enabled && Boolean(accountAddress) && Boolean(getAuthHeaders),
    // No standing poll: nothing here says a deposit is on its way. A delivery only becomes
    // observable once it has already settled, so there is no in-flight state to follow and a
    // timer would just bill one 1Click call per collector, forever, on the chance a user is
    // mid-send. The two moments a send is actually expected are covered directly — the deposit
    // panel refetches while it is showing an address, and returning to the tab refetches below,
    // which is the shape of this flow: users leave to send funds from another wallet.
    refetchInterval: (query) => (query.state.status === "error" ? ERROR_BACKOFF_MS : false),
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
    retry: 2,
  });
}
